import {Box} from '@mui/material';
import React, {useEffect, useState} from 'react';
import {Route, Routes, useLocation, useMatch, useNavigate, useParams} from "react-router-dom";
import GridView from "../../components/GridView";
import {AddRounded, AutoplayFilledRounded, StopCircleRounded} from "@mui-symbols-material/w300";
import AutoScrollTabs from "../../components/AutoScrollTabs";
import {useQuery} from "@apollo/client";
import {GridList} from "../../graphql/queries/gridList";
import _ from "lodash";
import CreateGridModal from "../../components/GridView/CreateGridModal";
import FullscreenToggleButton from "../../components/FullscreenToggleButton";
import {useTranslation} from "react-i18next";
import {Button, Tooltip} from "@mui/joy";
import {GridViewRotationTime} from "../../graphql/queries/gridViewRotationTime";


const GridViewDetailsScreen = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { gridViewId } = useParams<{ gridViewId: string }>();
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [autoCycle, setAutoCycle] = useState(false);
    const [, setCycleIndex] = useState(0);

    const gridViewQuery = useQuery(GridViewRotationTime, {
        variables: {
            id: gridViewId
        }
    })
    const rotationTime = _.get(gridViewQuery, 'data.gridView.rotationTime', null)
    const canLoop = !!rotationTime && rotationTime > 0;

    const gridListQuery = useQuery(GridList, {
        variables: {
            filter: {
                gridViewId: {equals: gridViewId}
            }
        },
        skip: !gridViewId
    })
    const grids = _.get(gridListQuery, 'data.gridList.grids', []);

    const match = useMatch("/gridViews/:gridViewId/grids/:gridId");
    const currentGridId = match?.params.gridId;
    const activeTab = currentGridId ?? (grids[0]?.id ?? "");

    useEffect(() => {
        if (grids.length > 0 && location.pathname === `/gridViews/${gridViewId}`) {
            navigate(`/gridViews/${gridViewId}/grids/${grids[0].id}`, { replace: true });
        }
    }, [grids, location.pathname, gridViewId, navigate]);

    useEffect(() => {
        if (!autoCycle || !canLoop || grids.length === 0) return;

        const interval = setInterval(() => {
            setCycleIndex((prevIndex) => {
                const nextIndex = (prevIndex + 1) % grids.length;
                navigate(`/gridViews/${gridViewId}/grids/${grids[nextIndex].id}`);
                return nextIndex;
            });
        }, rotationTime * 1000);

        return () => clearInterval(interval);
    }, [autoCycle, canLoop, rotationTime, grids, gridViewId, navigate]);

    const handleTabChange = (event: React.SyntheticEvent | null, value: number | string | null) => {
        if (value === 'create') {
            setCreateModalOpen(true);
        } else {
            navigate(`/gridViews/${gridViewId}/grids/${value}`);
        }
    };

    if (!gridViewId) return null;

    return (
        <Box sx={{ maxWidth: '100%' }} flexDirection="column" display={'flex'} flexGrow={1}>
            <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                <AutoScrollTabs
                    value={activeTab}
                    onChange={handleTabChange}
                    tabs={[
                        ...grids.map((grid: any) => ({ label: grid.title, value: grid.id })),
                        { label: '', value: 'create', icon: <AddRounded /> },
                    ]}
                />
                <Box>
                    <FullscreenToggleButton />
                    <Tooltip title={!canLoop && !autoCycle ? t("Set a rotation time greater than 0 to enable loop mode.") : ""}>
                        {/* span keeps the tooltip working while the button is disabled */}
                        <span>
                            <Button
                                startDecorator={autoCycle ? <StopCircleRounded /> : <AutoplayFilledRounded />}
                                sx={{ml: "8px"}}
                                disabled={!canLoop && !autoCycle}
                                onClick={() => setAutoCycle(!autoCycle)}
                            >
                                {autoCycle ? t("Stop") : t("Loop")}
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
            </Box>

            {
                grids.length === 0 ? (
                    <Box
                        flexGrow={1}
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                        textAlign="center"
                        sx={{ mt: 4 }}
                    >
                        <Box sx={{ fontSize: "1.2rem", mb: 2 }}>
                            {t("No systems available.")}
                        </Box>
                        <Button
                            startDecorator={<AddRounded />}
                            onClick={() => setCreateModalOpen(true)}
                        >
                            {t("Add system")}
                        </Button>
                    </Box>
                ) : (
                    <Routes>
                        <Route path={`grids/:gridId`} element={<GridView gridViewId={gridViewId} />} />
                    </Routes>
                )
            }

            {
                createModalOpen &&
                <CreateGridModal
                    gridViewId={gridViewId}
                    open={createModalOpen}
                    onClose={() => setCreateModalOpen(false)}
                />
            }
        </Box>
    );
};

export default GridViewDetailsScreen;
